import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { ShellHydration } from '../../../api.js';
import type { Palette } from '../../../_theme/palette.js';
import { WizardFooter } from '../../../_components/WizardFooter.js';
import { type Direction, pageAnim } from '../../../motion/keyframes.js';
import { useAutoAdvance } from '../hooks/useAutoAdvance.js';
import { usePageKeyboard } from '../hooks/usePageKeyboard.js';
import type { PublicSurvey, SurveyPage, SurveyQuestion } from '../types.js';
import { valueForLetterKey, valueForNumberKey } from '../utils/keyboardMap.js';
import { validateAnswer, validateAnswerDetailed } from '../utils/validation.js';
import { BrandingMark } from './BrandingMark.js';
import { IdentityGate } from './IdentityGate.js';
import { PageHeader } from './PageHeader.js';
import { QuestionCard } from './QuestionCard.js';

interface PagedSurveyProps {
  palette: Palette;
  survey: PublicSurvey;
  currentPage: SurveyPage;
  questions: SurveyQuestion[];
  hydration: ShellHydration;
  pageCounterSlot?: ReactNode;
  lateIdentityNeeded: boolean;
  emailRequired: boolean;
  email: string;
  setEmail: (email: string) => void;
  responses: Record<string, unknown>;
  setResponses: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  canGoBack: boolean;
  isLast: boolean;
  submitting: boolean;
  error: string | null;
  setError: (message: string | null) => void;
  onBack: () => void;
  onNext: () => Promise<void>;
  // Movement direction since the previous page — drives slide-in direction.
  direction: Direction;
  // Called when validation rejects user input on Next — drives the
  // validation_failed analytics event.
  onValidationFailed?: (questionKey: string, rule: string, message: string) => void;
}

export function PagedSurvey(props: PagedSurveyProps): JSX.Element {
  const {
    palette,
    survey,
    currentPage,
    questions,
    hydration,
    pageCounterSlot,
    lateIdentityNeeded,
    emailRequired,
    email,
    setEmail,
    responses,
    setResponses,
    canGoBack,
    isLast,
    submitting,
    error,
    setError,
    onBack,
    onNext,
    direction,
    onValidationFailed,
  } = props;

  const [attempted, setAttempted] = useState(false);
  const fieldErrors = computeFieldErrors(questions, responses);
  // Reset scroll on page change so respondents always land at the top of
  // the new page instead of inheriting the previous page's scroll position.
  // The actual scroll container is whichever ancestor sets `overflow: auto`
  // (SurveyShell's content area today) — walk up the tree until we find it.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    let parent: HTMLElement | null = node.parentElement;
    while (parent) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        parent.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }
      parent = parent.parentElement;
    }
  }, [currentPage.key]);

  const handleNext = async (): Promise<void> => {
    // Re-run detailed validation so we know which rule fired per question
    // and can emit a validation_failed event for analytics.
    let blocked = false;
    for (const question of questions) {
      const failure = validateAnswerDetailed(question, responses[question.key]);
      if (!failure) continue;
      blocked = true;
      onValidationFailed?.(question.key, failure.rule, failure.message);
    }
    if (blocked) {
      setAttempted(true);
      setError(null);
      return;
    }
    setAttempted(false);
    setError(null);
    await onNext();
  };

  // When a page has a single visible question we treat it like conversational
  // mode for keyboard + auto-advance: number/letter shortcuts are unambiguous,
  // and picking a value advances after a short delay. Multi-question pages
  // skip both — Enter to advance still works, but no auto-advance/shortcuts.
  const singleQuestion = questions.length === 1 && !lateIdentityNeeded ? questions[0] : null;
  const autoAdvanceEnabled = singleQuestion ? survey.appearance?.autoAdvance !== false : false;

  useAutoAdvance({
    question: singleQuestion,
    value: singleQuestion ? responses[singleQuestion.key] : undefined,
    enabled: autoAdvanceEnabled,
    onAdvance: () => void handleNext(),
  });

  const setSingleValue = (next: unknown): void => {
    if (!singleQuestion) return;
    setResponses((prev) => ({ ...prev, [singleQuestion.key]: next }));
  };

  usePageKeyboard({
    onEnter: () => void handleNext(),
    onNumberKey: singleQuestion
      ? (key) => {
          const v = valueForNumberKey(singleQuestion, key);
          if (v !== undefined) setSingleValue(v);
        }
      : undefined,
    onLetterKey: singleQuestion
      ? (index) => {
          const v = valueForLetterKey(singleQuestion, index);
          if (v !== undefined) setSingleValue(v);
        }
      : undefined,
  });

  return (
    <div ref={scrollRef} style={outerStyle()}>
      {/*
        Keying on currentPage.key forces React to remount when the page
        changes so the chirp-page-in keyframe replays. The whole content
        slides as one unit — staggering each question instead produces a
        "staircase" animation since the parent slide composes with per-
        child slides.
      */}
      <div
        key={currentPage.key}
        className="chirp-survey-page-body"
        style={{
          padding: '32px',
          paddingBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          animation: pageAnim(direction),
        }}
      >
        <PageHeader palette={palette} page={currentPage} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {questions.map((question, index) => (
            <QuestionCard
              key={question.key}
              palette={palette}
              question={question}
              indexOnPage={index}
              value={responses[question.key]}
              hydration={hydration}
              fieldError={attempted ? fieldErrors[question.key] : null}
              onChange={(value) => setResponses((prev) => ({ ...prev, [question.key]: value }))}
            />
          ))}
          {lateIdentityNeeded && (
            <IdentityGate palette={palette} identityConfig={survey.identityConfig} email={email} onEmailChange={setEmail} />
          )}
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
        <BrandingMark
          palette={palette}
          theme={survey.theme}
          hideBranding={survey.appearance?.hideBranding}
        />
      </div>
      <div style={stickyFooterStyle()}>
        <WizardFooter
          palette={palette}
          onBack={onBack}
          onNext={() => void handleNext()}
          canGoBack={canGoBack}
          isLast={isLast}
          submitting={submitting}
          submitLabel={emailRequired ? 'Email required' : 'Submit'}
          centerSlot={pageCounterSlot}
        />
      </div>
    </div>
  );
}

function outerStyle(): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  };
}

// Position-sticky pins the Back/Next bar to the bottom of the scrolling
// content area without lifting it out of normal flow. Works inside any
// overflow container so we don't have to coordinate with SurveyShell's
// own flex setup. Z-index sits above question cards so the bottom of a
// scrolled page never visually merges into the buttons.
function stickyFooterStyle(): CSSProperties {
  return {
    position: 'sticky',
    bottom: 0,
    zIndex: 5,
  };
}

function computeFieldErrors(
  questions: SurveyQuestion[],
  responses: Record<string, unknown>,
): Record<string, string | null> {
  const errors: Record<string, string | null> = {};
  for (const question of questions) {
    errors[question.key] = validateAnswer(question, responses[question.key]);
  }
  return errors;
}
